'use client';

import { type ReactNode, useState } from 'react';
import { ShowHide } from '@zillow/constellation-10';
import { showMoreContentStack } from './showMoreContent.styles';

type ShowMoreContentProps = {
  children: ReactNode;
  controlsId: string;
  deferHiddenContent?: boolean;
  preview?: ReactNode;
  showMoreLabel: string;
  showLessLabel: string;
  showMoreAriaLabel?: string;
  showLessAriaLabel?: string;
  buttonTestId?: string;
};

/**
 * HDP section adapter for Constellation ShowHide.
 *
 * `ShowHide.Root` requires a single wrapping child, and any
 * `ShowHide.Hidden` must be a direct child inside that wrapper.
 */
export const ShowMoreContent = ({
  children,
  controlsId,
  deferHiddenContent = false,
  preview,
  showMoreLabel,
  showLessLabel,
  showMoreAriaLabel,
  showLessAriaLabel,
  buttonTestId,
}: ShowMoreContentProps) => {
  const [isHidden, setIsHidden] = useState(true);
  const buttonLabel = isHidden ? showMoreLabel : showLessLabel;
  const ariaLabel = isHidden ? showMoreAriaLabel : showLessAriaLabel;
  const hiddenContent = deferHiddenContent && isHidden ? null : children;

  return (
    <ShowHide.Root
      id={controlsId}
      appearance="block"
      isHidden={isHidden}
      onDisplayChange={() => setIsHidden((prev) => !prev)}
      renderButton={() => (
        <ShowHide.Button aria-controls={controlsId} aria-label={ariaLabel ?? buttonLabel} data-testid={buttonTestId}>
          {buttonLabel}
        </ShowHide.Button>
      )}
    >
      <div className={showMoreContentStack}>
        {preview}
        <ShowHide.Hidden>{hiddenContent}</ShowHide.Hidden>
      </div>
    </ShowHide.Root>
  );
};
